import { useState, useEffect, useCallback } from "react";
import { getStrategyHoldings, type StrategyHolding } from "../../services/portfolioService";
import { runBacktest } from "../../services/api";
import { previewInvestment, type SkippedAllocation } from "../../services/executionService";

interface StrategyHoldingsProps {
  investmentId: string;
  strategyId: string;
  currentValue: number;
}

interface BacktestComparison {
  ticker: string;
  shadowPct: number;
  backtestPct: number;
  previewPct: number;
  difference: number;
  status: "match" | "mismatch" | "missing_shadow" | "missing_backtest" | "skipped_fractionable";
  skipReason?: string;
}

export function StrategyHoldings({ investmentId, strategyId, currentValue }: StrategyHoldingsProps) {
  const [holdings, setHoldings] = useState<StrategyHolding[]>([]);
  const [totalValue, setTotalValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Backtest comparison state
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState<BacktestComparison[] | null>(null);
  const [comparisonError, setComparisonError] = useState<string | null>(null);
  const [backtestDate, setBacktestDate] = useState<string | null>(null);

  const loadHoldings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getStrategyHoldings(investmentId);
      setHoldings(data.holdings || []);
      setTotalValue(data.total_value || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load holdings");
    } finally {
      setIsLoading(false);
    }
  }, [investmentId]);

  useEffect(() => {
    loadHoldings();
  }, [loadHoldings]);

  const formatCurrency = (value: number | string) => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatNumber = (value: number | string | undefined | null, decimals = 4) => {
    if (value === undefined || value === null) return "0.00";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: decimals,
    });
  };

  const formatPercent = (value: number | string | undefined | null) => {
    if (value === undefined || value === null) return "0.00%";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "0.00%";
    return `${num.toFixed(2)}%`;
  };

  // Compare shadow positions to backtest allocations
  const compareToBacktest = async () => {
    setIsComparing(true);
    setComparisonError(null);
    setComparisonData(null);
    
    try {
      // Step 1: Fetch preview to get intended trades, skipped allocations, and fractional setting
      let skippedMap = new Map<string, SkippedAllocation>();
      let previewMap = new Map<string, number>(); // ticker -> preview weight %
      let isFractionalAccount = true; // safe default if preview fails
      let previewTargetWeights: Record<string, number> | null = null;
      try {
        const preview = await previewInvestment(investmentId);
        isFractionalAccount = !!(preview.fractional_enabled);
        if (preview.skipped_allocations) {
          preview.skipped_allocations.forEach(s => skippedMap.set(s.symbol, s));
        }
        // Compute preview % from intended trades (tickers that need a trade)
        const allocVal = parseFloat(preview.allocated_value || "0");
        if (allocVal > 0 && preview.intended_trades) {
          preview.intended_trades.forEach(t => {
            const shares = parseFloat(t.target_shares || "0");
            const price = parseFloat(t.est_price || "0");
            const value = shares * price;
            previewMap.set(t.symbol, (value / allocVal) * 100);
          });
        }
        previewTargetWeights = preview.target_weights || null;
      } catch {
        // Preview may fail (market closed etc) - continue without preview info
      }

      // Step 2: Run backtest with matching whole-shares setting so Backtest %
      // reflects actual achievable allocations for non-fractional accounts
      const result = await runBacktest("", 252, {
        strategyId,
        useCache: true,
        useFastMode: true,
        initialCapital: currentValue || 100000,
        slippageBps: 1,
        includeRegulatoryFees: true,
        wholeSharesOnly: !isFractionalAccount,
      });
      
      // Get the most recent allocation (today or last trading day)
      const allocations = result.allocations || [];
      if (allocations.length === 0) {
        throw new Error("No allocation data available from backtest");
      }
      
      // Get the last allocation date (most recent)
      const latestAllocation = allocations[allocations.length - 1];
      setBacktestDate(latestAllocation.date);

      // Step 3: Build shadow map from current holdings
      const shadowMap = new Map<string, number>();
      holdings.forEach(h => {
        if (h.symbol !== "$CASH") {
          const pct = typeof h.weight_pct === "string" ? parseFloat(h.weight_pct) : h.weight_pct;
          shadowMap.set(h.symbol, isNaN(pct) ? 0 : pct);
        }
      });

      // Step 4: Fill Preview % for at-target tickers that had no trade needed.
      // When fractionalEnabled=false, many positions won't appear in intended_trades
      // because floor(target) == floor(current). These positions ARE at target —
      // preview would not change them, so Preview % = current shadow %.
      if (previewTargetWeights) {
        for (const ticker of Object.keys(previewTargetWeights)) {
          if (!previewMap.has(ticker) && !skippedMap.has(ticker) && shadowMap.has(ticker)) {
            previewMap.set(ticker, shadowMap.get(ticker)!);
          }
        }
      }
      
      // Step 5: Build backtest map (skip CASH — whole-shares backtest includes cash weight)
      const backtestMap = new Map<string, number>();
      Object.entries(latestAllocation.allocations).forEach(([ticker, weight]) => {
        if (ticker === "CASH" || ticker === "$CASH") return;
        const pct = typeof weight === "string" ? parseFloat(weight) : (weight as number);
        backtestMap.set(ticker, (isNaN(pct) ? 0 : pct) * 100); // Convert to percentage
      });
      
      // Combine all tickers
      const allTickers = new Set([...shadowMap.keys(), ...backtestMap.keys(), ...previewMap.keys()]);
      
      const comparison: BacktestComparison[] = [];
      allTickers.forEach(ticker => {
        const shadowPct = shadowMap.get(ticker) || 0;
        const backtestPct = backtestMap.get(ticker) || 0;
        const previewPct = previewMap.get(ticker) || 0;
        const difference = shadowPct - previewPct; // Diff against what we'd actually trade
        
        let status: BacktestComparison["status"];
        let skipReason: string | undefined;
        if (shadowPct === 0 && skippedMap.has(ticker)) {
          status = "skipped_fractionable";
          skipReason = skippedMap.get(ticker)!.reason;
        } else if (shadowPct === 0) {
          status = "missing_shadow";
        } else if (backtestPct === 0) {
          status = "missing_backtest";
        } else if (Math.abs(difference) <= 2) {
          status = "match";
        } else {
          status = "mismatch";
        }
        
        comparison.push({ ticker, shadowPct, backtestPct, previewPct, difference, status, skipReason });
      });
      
      // Sort by absolute difference (biggest mismatches first)
      comparison.sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
      
      setComparisonData(comparison);
    } catch (err) {
      setComparisonError(err instanceof Error ? err.message : "Failed to run backtest comparison");
    } finally {
      setIsComparing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-surface-800 rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-400 mb-2">{error}</div>
        <button
          onClick={loadHoldings}
          className="text-sm text-primary-400 hover:text-primary-300"
        >
          Try again
        </button>
      </div>
    );
  }

  if (holdings.length === 0) {
    return (
      <div className="text-center py-8 text-surface-400">
        <svg className="w-12 h-12 mx-auto mb-3 text-surface-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <p>No holdings yet</p>
        <p className="text-sm mt-1">Execute the strategy to see holdings</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left text-xs text-surface-400 border-b border-surface-700/50">
            <th className="pb-3 pr-4 font-medium">Ticker</th>
            <th className="pb-3 px-4 font-medium text-right">Cost Basis</th>
            <th className="pb-3 px-4 font-medium text-right">Current Price</th>
            <th className="pb-3 px-4 font-medium text-right">Quantity</th>
            <th className="pb-3 px-4 font-medium text-right">Current Value</th>
            <th className="pb-3 pl-4 font-medium text-right">Weight</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-800">
          {/* Holdings Rows */}
          {holdings.map((holding) => {
            const isCash = holding.symbol === "$CASH";
            return (
            <tr key={holding.symbol} className="hover:bg-surface-800/30 transition-colors">
              <td className="py-3 pr-4">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isCash ? 'bg-green-900/50' : 'bg-surface-700'}`}>
                    <span className={`text-xs font-bold ${isCash ? 'text-green-400' : 'text-surface-300'}`}>
                      {isCash ? '$' : holding.symbol.slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-surface-200">{isCash ? 'Cash' : holding.symbol}</div>
                    <div className="text-xs text-surface-500 truncate max-w-[200px]">
                      {isCash ? 'Unallocated funds' : (holding.name || holding.asset_type)}
                    </div>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4 text-right text-surface-300">
                {isCash ? '—' : formatCurrency(holding.avg_cost)}
              </td>
              <td className="py-3 px-4 text-right text-surface-300">
                {isCash ? '—' : formatCurrency(holding.current_price)}
              </td>
              <td className="py-3 px-4 text-right text-surface-300">
                {isCash ? '—' : formatNumber(holding.quantity)}
              </td>
              <td className="py-3 px-4 text-right text-surface-300">
                {formatCurrency(holding.current_value)}
              </td>
              <td className="py-3 pl-4 text-right">
                <div className="flex items-center justify-end gap-2">
                  <div className="w-16 h-1.5 rounded-full bg-surface-700 overflow-hidden">
                    <div
                      className={`h-full ${isCash ? 'bg-green-500/50' : 'bg-primary-500/50'}`}
                      style={{ width: `${Math.min(holding.weight_pct, 100)}%` }}
                    />
                  </div>
                  <span className="text-surface-300 text-sm min-w-[50px]">
                    {formatPercent(holding.weight_pct)}
                  </span>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>

      {/* Total */}
      <div className="flex justify-between items-center pt-4 mt-4 border-t border-surface-700/50">
        <span className="text-sm text-surface-400">Total Value</span>
        <span className="text-lg font-semibold text-surface-100">{formatCurrency(totalValue)}</span>
      </div>

      {/* Compare to Backtest Button */}
      <div className="mt-6 pt-4 border-t border-surface-700/50">
        <button
          onClick={compareToBacktest}
          disabled={isComparing || holdings.length === 0}
          className="w-full sm:w-auto px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:bg-surface-700 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
        >
          {isComparing ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Running Backtest...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Compare to Backtest
            </>
          )}
        </button>

        {/* Comparison Error */}
        {comparisonError && (
          <div className="mt-3 p-3 rounded-lg bg-red-900/30 border border-red-700/50 text-red-400 text-sm">
            {comparisonError}
          </div>
        )}

        {/* Comparison Results */}
        {comparisonData && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-surface-200">
                Backtest Comparison
              </h4>
              {backtestDate && (
                <span className="text-xs text-surface-400">
                  Backtest date: {backtestDate}
                </span>
              )}
            </div>
            
            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
              <div className="p-2 rounded-lg bg-green-900/20 border border-green-700/30">
                <div className="text-lg font-semibold text-green-400">
                  {comparisonData.filter(c => c.status === "match").length}
                </div>
                <div className="text-xs text-green-500">Matching</div>
              </div>
              <div className="p-2 rounded-lg bg-red-900/20 border border-red-700/30">
                <div className="text-lg font-semibold text-red-400">
                  {comparisonData.filter(c => c.status === "mismatch").length}
                </div>
                <div className="text-xs text-red-500">Mismatched</div>
              </div>
              <div className="p-2 rounded-lg bg-yellow-900/20 border border-yellow-700/30">
                <div className="text-lg font-semibold text-yellow-400">
                  {comparisonData.filter(c => c.status === "missing_shadow").length}
                </div>
                <div className="text-xs text-yellow-500">Missing from Shadow</div>
              </div>
              <div className="p-2 rounded-lg bg-purple-900/20 border border-purple-700/30">
                <div className="text-lg font-semibold text-purple-400">
                  {comparisonData.filter(c => c.status === "skipped_fractionable").length}
                </div>
                <div className="text-xs text-purple-500">Skipped (Not Fractionable)</div>
              </div>
              <div className="p-2 rounded-lg bg-orange-900/20 border border-orange-700/30">
                <div className="text-lg font-semibold text-orange-400">
                  {comparisonData.filter(c => c.status === "missing_backtest").length}
                </div>
                <div className="text-xs text-orange-500">Extra in Shadow</div>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-surface-400 border-b border-surface-700/50">
                    <th className="pb-2 pr-4 font-medium">Ticker</th>
                    <th className="pb-2 px-4 font-medium text-right">Shadow %</th>
                    <th className="pb-2 px-4 font-medium text-right">Backtest %</th>
                    <th className="pb-2 px-4 font-medium text-right">Preview %</th>
                    <th className="pb-2 px-4 font-medium text-right">Drift</th>
                    <th className="pb-2 pl-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-800">
                  {comparisonData.map((row) => (
                    <tr key={row.ticker} className="hover:bg-surface-800/30">
                      <td className="py-2 pr-4 font-medium text-surface-200">{row.ticker}</td>
                      <td className="py-2 px-4 text-right text-surface-300">
                        {row.shadowPct > 0 ? `${row.shadowPct.toFixed(2)}%` : "—"}
                      </td>
                      <td className="py-2 px-4 text-right text-surface-300">
                        {row.backtestPct > 0 ? `${row.backtestPct.toFixed(2)}%` : "—"}
                      </td>
                      <td className="py-2 px-4 text-right text-surface-300">
                        {row.previewPct > 0 ? `${row.previewPct.toFixed(2)}%` : row.status === "skipped_fractionable" ? "0%" : "—"}
                      </td>
                      <td className={`py-2 px-4 text-right font-medium ${
                        row.difference > 0 ? "text-green-400" : row.difference < 0 ? "text-red-400" : "text-surface-400"
                      }`}>
                        {row.difference > 0 ? "+" : ""}{row.difference.toFixed(2)}%
                      </td>
                      <td className="py-2 pl-4">
                        <div className="flex flex-col gap-0.5">
                        {row.status === "match" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900/50 text-green-400 w-fit">
                            ✓ Match
                          </span>
                        )}
                        {row.status === "mismatch" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-900/50 text-red-400 w-fit">
                            ✗ Mismatch
                          </span>
                        )}
                        {row.status === "missing_shadow" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-900/50 text-yellow-400 w-fit">
                            Missing
                          </span>
                        )}
                        {row.status === "missing_backtest" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-900/50 text-orange-400 w-fit">
                            Extra
                          </span>
                        )}
                        {row.status === "skipped_fractionable" && (
                          <>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-900/50 text-purple-400 w-fit">
                              ⚠ Skipped
                            </span>
                            {row.skipReason && (
                              <span className="text-[10px] text-purple-400/70 max-w-[200px]">
                                {row.skipReason}
                              </span>
                            )}
                          </>
                        )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
